import closeSvg from 'assets/icons/close.svg';
import { VALID_EMAIL_REGEX } from 'constants/email';
import Button, { Color, Size } from 'lib/Button';
import Dropdown from 'lib/Dropdown';
import Input from 'lib/Input';
import IconButton from 'lib/IconButton';
import { ButtonSize } from 'lib/IconButton/IconButton.types';
import { InputType, InputSize } from 'components/common/types/Input.types';
import { Period, PERIOD_DATA_LIST } from './useEmailNotificationApi';

interface NotificationPanelProps {
  isEnabled: boolean;
  isSaving: boolean;
  draftEmail: string;
  draftPeriod: Period;
  savedEmail: string;
  savedPeriod: Period;
  marketTimezone: string;
  onEmailChange: (email: string) => void;
  onPeriodChange: (period: Period) => void;
  onSave: () => void;
  onCancel: () => void;
  onUnsubscribe: () => void;
}

const NotificationPanel = ({
  isEnabled,
  isSaving,
  draftEmail,
  draftPeriod,
  savedEmail,
  savedPeriod,
  marketTimezone,
  onEmailChange,
  onPeriodChange,
  onSave,
  onCancel,
  onUnsubscribe,
}: NotificationPanelProps): React.ReactElement => (
  <div className="w-[340px] bg-white rounded-lg shadow-lg border border-neutral-200">
    <div className="px-4 pt-4 pb-3 relative">
      <div className="absolute top-3 right-3">
        <IconButton
          icon={closeSvg}
          name="close-notification"
          dataTestId="notification-close"
          buttonSize={ButtonSize.SMALL}
          handleOnClick={onCancel}
        />
      </div>
      <p className="sub-header-base text-essential-primary mb-0.5 pr-5">Email notifications</p>
      <p className="body-base text-essential-tertiary mb-3">
        {`Get notified daily (Mon-Sun) at 8:30, 12:30 and 16:30${marketTimezone ? ` (${marketTimezone})` : ''} about creatives pending review.`}
      </p>

      <div className="body-base text-essential-secondary leading-relaxed space-y-2">
        <div>
          <span>Notify</span>
          <Input
            type={InputType.EMAIL}
            size={InputSize.SMALL}
            value={draftEmail}
            onChange={(value) => onEmailChange(String(value ?? ''))}
            placeholder="Enter email address"
            dataTestId="notification-email"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span>For creatives pending review, uploaded within</span>
        </div>

        <div>
          <Dropdown
            name="notification-period"
            dataTestId="notification-period"
            dataList={PERIOD_DATA_LIST}
            value={draftPeriod}
            onChange={(newValue) => onPeriodChange(newValue as Period)}
            size={InputSize.SMALL}
          />
        </div>
      </div>
    </div>

    <div className="flex justify-between gap-2 px-4 py-2.5 bg-neutral-50 border-t border-neutral-200 rounded-b-lg">
      <Button
        dataTestId={isEnabled ? 'notification-unsubscribe' : 'notification-cancel'}
        size={Size.MEDIUM}
        color={isEnabled ? Color.DANGER : Color.SECONDARY}
        label={isEnabled ? 'Unsubscribe' : 'Cancel'}
        onClick={isEnabled ? onUnsubscribe : onCancel}
        isDisabled={isSaving}
      />
      <Button
        dataTestId="notification-save"
        size={Size.MEDIUM}
        color={Color.PRIMARY}
        label={isEnabled ? 'Save' : 'Subscribe'}
        onClick={onSave}
        isDisabled={
          isSaving ||
          !draftEmail.trim() ||
          !VALID_EMAIL_REGEX.test(draftEmail.trim()) ||
          (isEnabled && draftEmail === savedEmail && draftPeriod === savedPeriod)
        }
      />
    </div>
  </div>
);

export default NotificationPanel;
